import { FileAddOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Col, Descriptions, Form, message, Row } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TOUR_STORIES } from '../../../../constants/define/translation';
import { postCreateTourStoryRepository } from '../../../../repository/mytm/tourStory/tourStoryRepository';
import { appStyles, formScrolling, selectSelector } from '../../../../styles';
import { TourStoryType } from '../../../../types/tourStory';
import { CONFIG_CONTAINER_NAME, uploadFileToBlob, UploadFileToBlobInterface } from '../../../../utils/azure';
import { ButtonActionPage, SpinLoading } from '../../../atoms';
import { PreviewTourStory } from '../../../molecules/preview/common/PreviewTourStory';
import { DragDropImage } from '../../../molecules/upload';

interface FormTourStoryProps {
  record: TourStoryType;
  onRefresh: () => void;
  onCloseModal: () => void;
  setDataRecord: Function;
  sortOrderMax: number;
}

const azureCDN = process.env.REACT_APP_AZURE_CDN;
const storageAccountName = process.env.REACT_APP_AZURE_BLOB_ACCOUNT_NAME;

export const FormTourStory = (props: FormTourStoryProps) => {
  const { t } = useTranslation(TOUR_STORIES);
  const { record, onRefresh, onCloseModal, setDataRecord, sortOrderMax } = props;
  const [form] = Form.useForm();
  const defaultValue = {
    url: record?.url || '',
  };
  const [isCreateLoading, setCreateLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<number>(0);
  const [imageList, setImageList] = useState<UploadFileToBlobInterface[]>([]);
  const [messageErrorUploadImage, setMessageErrorUploadImage] = useState<string>();

  useEffect(() => {
    if (imageList.length) {
      setMessageErrorUploadImage('');
    }
  }, [imageList]);

  const onFinish = useCallback(
    (formValue: any) => {
      if (!imageList.length || messageErrorUploadImage) {
        setMessageErrorUploadImage(t('validate.imageBlank'));
        return;
      }
      setCreateLoading(true);
      const imgList = imageList.filter(img => !img.isImageURL);
      const imgLink = imageList.filter(img => img.isImageURL);

      uploadFileToBlob(imgList, CONFIG_CONTAINER_NAME.UPLOADS)
        .then((images: any[]) => {
          let newImagesCDN = images;
          if (azureCDN) {
            newImagesCDN = images.map(url => url.replace(`${storageAccountName}.blob.core.windows.net`, azureCDN));
          }
          const url = newImagesCDN && newImagesCDN.length ? newImagesCDN[0] : imgLink.map(v => v.dataURL)[0];

          const newOptions = {};
          const payloadToPost = {
            type: 'IMAGE',
            url,
            options: newOptions,
            countries: 'USA',
            sortOrder: sortOrderMax || 1,
          };

          return postCreateTourStoryRepository(payloadToPost)
            .then(() => {
              onRefresh();
              onCloseModal();
              message.success(t('message.success.createTourStory'));
            })
            .catch(e => {
              setCreateLoading(true);
              message.error(e?.errorMessage);
            });
        })
        .catch(() => {
          setCreateLoading(true);
          message.error(t('message.error.uploadImage'));
        })
        .finally(() => {
          setCreateLoading(false);
          setDataRecord(null);
        });
    },
    [record, imageList],
  );

  const onSetImageList = useCallback((data: UploadFileToBlobInterface[]) => {
    setImageList(data);
  }, []);

  const onSelectedImage = useCallback((index: number) => {
    setSelectedImage(index);
  }, []);

  return (
    <>
      <SpinLoading isLoading={isCreateLoading} absolute>
        <SwitchContainer>
          <Form form={form} layout="vertical" initialValues={defaultValue} onFinish={onFinish} name="from-tourStory">
            <Row className="form-tourStory-container" gutter={[24, 24]}>
              <StyledFormTourStory>
                <div className="form-scroll">
                  <Descriptions />
                  <StyledSelectImageCol>
                    <DragDropImage
                      {...{
                        imageList,
                        onSetImageList,
                        messageErrorUploadImage,
                        selectedImage,
                        onSelectedImage,
                        maxImage: 1,
                        nodePreview: (
                          <PreviewTourStory
                            values={{
                              backgroundImage: imageList?.length && imageList[0].dataURL,
                              describeText: t('form.imageUpload'),
                              describeText2: t('form.image3x4'),
                            }}
                            isPreview
                            className="preview-tour-story"
                            height="330px"
                          />
                        ),
                      }}
                    />
                  </StyledSelectImageCol>

                  <Row style={{ justifyContent: 'flex-end' }}>
                    <BtnContainer>
                      <ButtonActionPage
                        loading={isCreateLoading}
                        text={t('form.done')}
                        id="1"
                        className="btn-submit-form-home"
                        htmlType="submit"
                        icon={!record ? <FileAddOutlined /> : undefined}
                      />
                    </BtnContainer>
                  </Row>
                </div>
              </StyledFormTourStory>
            </Row>
          </Form>
        </SwitchContainer>
      </SpinLoading>
    </>
  );
};

const StyledSelectImageCol = styled(Col)`
  min-height: 430px;
  height: 430px;
`;

const SwitchContainer = styled.div`
  & .ant-form-item-label {
    font-weight: bold;
    font-size: 15px;
  }

  & .form-tourStory-container {
    padding: 0 1.5rem;

    .form-tourStory__description {
      resize: none;
    }
  }

  & .preview-product-tile {
    border-radius: 18px;
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  }
`;

const BtnContainer = styled.div`
  & .btn-submit-form-home {
    width: 160px;
    bottom: -15px !important;
    right: -25px !important;
  }
`;

const StyledFormTourStory = styled(Col)`
  ${formScrolling}
  ${selectSelector}

  & input[type='text'],
  & input[type='number'] {
    ${appStyles.inputStyle}
  }

  & .add-partner {
    float: right;
    color: #3490ff;
    font-weight: bold;
    font-size: 14px;
    position: absolute;
    right: 0;
    z-index: 9999;
    background-color: transparent;
    border: none;
    cursor: pointer;
    text-decoration: underline;
    display: inline;
  }

  & .ant-form-item-label {
    font-weight: bold;
    font-size: 16px;
  }

  & button.btn-submit-form-home {
    ${appStyles.commonButtonSubmitted}
  }

  & .ant-form-item {
    margin-bottom: 0.5rem;
  }

  & .ant-picker {
    width: 100%;
    padding: 0;
    ${appStyles.commonBorderRadius}
    & input {
      padding: 4px 0 4px 12px;
    }
    & .ant-picker-suffix {
      font-size: 20px;
      ${appStyles.background.black}
      ${appStyles.color.white}
      padding: 8px;
    }
  }

  & textarea {
    ${appStyles.commonBorderRadius}
  }

  & .ant-input-affix-wrapper {
    padding: 0 11px;
    ${appStyles.commonBorderRadius}
  }

  & .ant-form-item {
    ${appStyles.commonItemMarginBottom}
  }

  & .ant-form-item-explain {
    margin: 0;
  }

  & .ant-picker-clear {
    right: 40px;
  }

  & .ant-descriptions {
    cursor: default;
    & .ant-descriptions-header {
      margin-bottom: 5px;
    }
  }

  & .popover {
    position: absolute;
    z-index: 2;
    bottom: 0;
    left: 10%;
  }

  & .popoverSub {
    position: absolute;
    z-index: 2;
    bottom: 0;
    right: 0;
  }

  & .cover {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  & .sketch-picker {
    cursor: pointer;
  }

  & .error-upload {
    ${appStyles.color.error}
    position: absolute;
    bottom: 20;
    left: 0;
    font-size: 14px;
    & .anticon {
      margin-right: 10px;
    }
  }
`;
